import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, FolderOpen, Database, Wrench, X } from 'lucide-react';
import Select from 'react-select';
import api from '../../services/api';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';
import commonStyles from '../../styles/common.module.css';

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_name: '',
    client_description: '',
    service_centers: [],
    db_alias: '',
  });
  const [serviceCenters, setServiceCenters] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
    fetchServiceCenters();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/projects/');
      setProjects(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки проектов');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceCenters = async () => {
    try {
      const response = await api.get('/settings/service-centers/');
      setServiceCenters(
        response.data.map(sc => ({
          value: sc.id,
          label: sc.name,
        }))
      );
    } catch (error) {
      toast.error('Ошибка загрузки сервисных центров');
    }
  };

  useEffect(() => {
    if (!pendingEdit) return;
    if (serviceCenters.length === 0) return;

    const selectedCenters = serviceCenters.filter(scOption =>
      pendingEdit.service_centers.includes(scOption.value)
    );

    setFormData({
      name: pendingEdit.name,
      description: pendingEdit.description || '',
      client_name: pendingEdit.client_name || '',
      client_description: pendingEdit.client_description || '',
      service_centers: selectedCenters,
      db_alias: pendingEdit.db_alias,
    });
    setPendingEdit(null);
  }, [pendingEdit, serviceCenters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      name: formData.name,
      description: formData.description,
      client_name: formData.client_name,
      client_description: formData.client_description,
      service_centers: formData.service_centers.map(sc => sc.value),
      db_alias: formData.db_alias,
    };

    try {
      if (editingId) {
        await api.put(`/settings/projects/${editingId}/`, data);
        toast.success('Проект обновлён');
      } else {
        await api.post('/settings/projects/', data);
        toast.success('Проект создан');
      }
      closeModal();
      fetchProjects();
    } catch (error) {
      toast.error('Ошибка сохранения проекта');
    }
  };

  const handleEdit = (project) => {
    setPendingEdit(project);
    setEditingId(project.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить проект?')) {
      try {
        await api.delete(`/settings/projects/${id}/`);
        toast.success('Проект удалён');
        fetchProjects();
      } catch (error) {
        toast.error('Ошибка удаления проекта');
      }
    }
  };

  const openAddModal = () => {
    setFormData({
      name: '',
      description: '',
      client_name: '',
      client_description: '',
      service_centers: [],
      db_alias: '',
    });
    setEditingId(null);
    setPendingEdit(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      name: '',
      description: '',
      client_name: '',
      client_description: '',
      service_centers: [],
      db_alias: '',
    });
    setEditingId(null);
    setPendingEdit(null);
  };

  const getServiceCenterNames = (scIds) => {
    if (!scIds || scIds.length === 0) return '—';
    return scIds
      .map(scId => {
        const found = serviceCenters.find(sc => sc.value === scId);
        return found ? found.label : '—';
      })
      .join(', ');
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка проектов...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <FolderOpen size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Проекты
        </h3>
        <button className={styles.addButton} onClick={openAddModal}>
          <Plus size={18} />
          Добавить проект
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {projects.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <FolderOpen size={48} />
            </div>
            <h3>Нет проектов</h3>
            <p>Добавьте первый проект для начала работы</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Описание</th>
                <th>Для клиента</th>
                <th>Алиас БД</th>
                <th>Сервисные центры</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id} className={styles.clickableRow} onClick={() => handleEdit(project)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FolderOpen size={16} style={{ color: '#0f766e' }} />
                      <strong>{project.name}</strong>
                    </div>
                  </td>
                  <td style={{ maxWidth: '300px', fontSize: '0.8125rem', lineHeight: '1.4' }}>
                    {project.description || (
                      <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                        Описание не указано
                      </span>
                    )}
                  </td>
                  <td style={{ maxWidth: '300px', fontSize: '0.8125rem', lineHeight: '1.4' }}>
                    <strong>{project.client_name || 'Заявки на ремонт'}</strong>
                    <div style={{ color: '#64748b', marginTop: '0.25rem' }}>
                      {project.client_description || 'Описание для клиента не указано'}
                    </div>
                  </td>
                  <td>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                      color: '#1e40af',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      width: 'fit-content'
                    }}>
                      <Database size={14} />
                      {project.db_alias}
                    </div>
                  </td>
                  <td style={{ maxWidth: '250px', fontSize: '0.8125rem' }}>
                    {getServiceCenterNames(project.service_centers)}
                  </td>
                  <td>
                    <button
                      className={styles.actionButton}
                      onClick={(event) => { event.stopPropagation(); handleDelete(project.id); }}
                      title="Удалить"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <FolderOpen size={20} />
                {editingId ? 'Редактировать проект' : 'Добавить проект'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>
                    <FolderOpen size={16} />
                    Название проекта
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Введите название проекта"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <FolderOpen size={16} />
                    Описание проекта
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Введите описание проекта"
                    rows={3}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <FolderOpen size={16} />
                    Название для клиента
                  </label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Например: Заявки на ремонт POS"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <FolderOpen size={16} />
                    Описание для клиента
                  </label>
                  <textarea
                    value={formData.client_description}
                    onChange={(e) => setFormData({ ...formData, client_description: e.target.value })}
                    placeholder="Короткое описание раздела для клиентского кабинета"
                    rows={3}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Wrench size={16} />
                    Сервисные центры
                  </label>
                  <div className={styles.select}>
                    <Select
                      isMulti
                      options={serviceCenters}
                      value={formData.service_centers}
                      onChange={(selected) =>
                        setFormData({ ...formData, service_centers: selected || [] })
                      }
                      placeholder={
                        serviceCenters.length
                          ? 'Выберите сервисные центры...'
                          : 'Загрузка...'
                      }
                      isDisabled={!serviceCenters.length}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Database size={16} />
                    Алиас БД
                  </label>
                  <input
                    type="text"
                    value={formData.db_alias}
                    onChange={(e) => setFormData({ ...formData, db_alias: e.target.value })}
                    placeholder="Введите алиас базы данных"
                    required
                  />
                </div>

                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Обновить проект' : 'Создать проект'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectList;
